/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
// React
import { useState, useEffect } from 'react';

// Mantine
import { Box, Group, Stack } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

// Components
import {
	Divider,
	Link,
	NumericInput,
	DatePicker,
	TextInput,
	Text,
} from '@components/atoms';
import { MultiSelect } from '@components/molecules';

// Utils
import { getUnitLabel } from '@utils/units';

// i18n
import { useTranslation } from 'react-i18next';

// Day js
import dayjs from 'dayjs';

// Styles
import classes from './FormAddProduction.module.css';

// Store
import useStore from '@store/index';

// Types
import { TSelectOption } from '@global-types/index';
import { TProduction } from '@pages/Productions/types';

const options: TSelectOption[] = [
	{ label: 'global.endDate', value: 'endDate' },
	{ label: 'global.proof', value: 'proof' },
	{
		label: 'global.conversionFactor',
		value: 'conversionFactor',
	},
];

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */
type TSingleFormProductionProps = {
	item: TProduction;
	index: number;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	form: any;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const SingleFormProduction = (
	props: TSingleFormProductionProps
) => {
	const isBigScreen = useMediaQuery('(min-width: 120em)');
	const { t } = useTranslation();

	/* ******************************** STATES ******************************* */

	const {
		setProductionsDefaultSelectedFields,
		productionsDefaultSelectedFields,
	} = useStore();

	const [activeAppliedFields, setActiveAppliedFields] =
		useState<TSelectOption[] | []>([]);

	const getProductionUnit = () => {
		switch (props.form.getValues().unitProduction) {
			case 'MWH':
				return getUnitLabel('mwh');
			case 'KWH':
				return getUnitLabel('kwh');
			case 't':
				return getUnitLabel('t');
			case 'T':
				return getUnitLabel('t');
			case 'kg':
				return getUnitLabel('kg');
			case 'KG':
				return getUnitLabel('kg');
			default:
				break;
		}
	};
	const getConversionFactorPlaceholder = () => {
		switch (props.form.getValues().unitProduction) {
			case 'MWH':
				return getUnitLabel('gCO2perKwh');
			case 'KWH':
				return getUnitLabel('gCO2perKwh');
			case 't':
				return getUnitLabel('gCO2perKg');
			case 'T':
				return getUnitLabel('gCO2perKg');
			case 'kg':
				return getUnitLabel('gCO2perKg');
			case 'KG':
				return getUnitLabel('gCO2perKg');
			default:
				break;
		}
	};

	const getStepQuantity = () => {
		switch (props.form.getValues().unitProduction) {
			case 'MWH':
				return 0.1;
			case 'KWH':
				return 1;
			case 't':
				return 0.1;
			case 'T':
				return 0.1;
			case 'kg':
				return 1;
			case 'KG':
				return 1;
			default:
				break;
		}
	};

	useEffect(() => {
		if (activeAppliedFields && props.index === 0) {
			setProductionsDefaultSelectedFields(
				activeAppliedFields
			);
		}
	}, [activeAppliedFields]);

	useEffect(() => {
		setActiveAppliedFields(
			productionsDefaultSelectedFields
		);
	}, []);

	/* ******************************** RENDERING ******************************* */
	return (
		<Stack key={props.item.id}>
			<Box className={classes.rowHeader}>
				<Group>
					<Text type="subtitle1">
						{t('global.production')} {props.index + 1}
					</Text>
					<MultiSelect
						optionType={t('global.fields')}
						initialValue={
							productionsDefaultSelectedFields
						}
						options={options}
						value={activeAppliedFields}
						handleapply={(fields: TSelectOption[]) => {
							setActiveAppliedFields(fields);
						}}
					/>
				</Group>
				{props.index !== 0 && (
					<Link
						size="sm"
						onClick={() =>
							props.form.removeListItem(
								'productions',
								props.index
							)
						}
						c="error"
						underline="always"
					>
						{t('productions.deleteProduction')}
					</Link>
				)}
			</Box>
			<Box className={classes.row}>
				<Box className={classes.containerInput}>
					<NumericInput
						unit={getProductionUnit()}
						withAsterisk
						label={t('global.production')}
						size={isBigScreen ? 'md' : 'sm'}
						step={getStepQuantity()}
						key={
							props.form &&
							props.form.key &&
							props.form.key(
								`productions.${props.index}.quantity`
							)
						}
						{...props.form.getInputProps(
							`productions.${props.index}.quantity`
						)}
					/>
				</Box>
				<Box className={classes.containerInput}>
					<DatePicker
						withAsterisk
						clearable
						size={isBigScreen ? 'lg' : 'sm'}
						label={t('global.startDate')}
						placeholder={t('global.selectDate')}
						maxDate={
							props.form.getInputProps(
								`productions.${props.index}.endDate`
							).value == null
								? dayjs().subtract(1, 'day').toDate()
								: dayjs(
										props.form.getInputProps(
											`productions.${props.index}.endDate`
										).value
									).toDate()
						}
						onChange={() => {}}
						{...props.form.getInputProps(
							`productions.${props.index}.startDate`
						)}
					/>
				</Box>
			</Box>
			<Box className={classes.row}>
				{/* End date */}
				{activeAppliedFields.find(
					(item) => item.value == 'endDate'
				) && (
					<Box className={classes.containerInput}>
						<DatePicker
							clearable
							size={isBigScreen ? 'lg' : 'sm'}
							label={t('global.endDate')}
							placeholder={t('global.selectDate')}
							minDate={
								props.form.getInputProps(
									`productions.${props.index}.startDate`
								).value &&
								dayjs(
									props.form.getInputProps(
										`productions.${props.index}.startDate`
									).value
								).toDate()
							}
							maxDate={dayjs()
								.subtract(1, 'day')
								.toDate()}
							onChange={() => {}}
							{...props.form.getInputProps(
								`productions.${props.index}.endDate`
							)}
						/>
					</Box>
				)}
				{/* ConversionFactor */}
				{activeAppliedFields.find(
					(item) => item.value == 'conversionFactor'
				) && (
					<Box className={classes.containerInput}>
						<NumericInput
							label={t('global.conversionFactor')}
							size={isBigScreen ? 'md' : 'sm'}
							unit={getConversionFactorPlaceholder()}
							step={1}
							{...props.form.getInputProps(
								`productions.${props.index}.conversionFactorOverride`
							)}
						/>
					</Box>
				)}
			</Box>

			{/* Proof */}
			{activeAppliedFields.find(
				(item) => item.value == 'proof'
			) && (
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<TextInput
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.proof')}
							placeholder={'uuid'}
							{...props.form.getInputProps(
								`productions.${props.index}.proof`
							)}
						/>
					</Box>
				</Box>
			)}

			<Divider size={'xs'} />
		</Stack>
	);
};

export default SingleFormProduction;
